import { Link } from "@tanstack/react-router";
import { Section, EyebrowHeading } from "./Section";
import { ArrowRight } from "lucide-react";
import { useLocale } from "@/i18n/LocaleProvider";
import { videoThumb } from "@/lib/media/thumbs";

interface Metric { label: string; value: string }
interface Item {
  id: string;
  client: string;
  title: string;
  impact: string;
  tag: string;
  metrics?: Metric[];
}

const itemsSv: Item[] = [
  { id: "9iiyNS4yNzA", client: "Garageportexperten", title: "Brand storytelling som öppnar dörrar", impact: "Löpande Prime-spår — film som används i sälj, on-boarding och rekrytering.", tag: "Prime · Brand", metrics: [{ label: "Användning", value: "3 kanaler" }, { label: "Tempo", value: "Löpande" }] },
  { id: "yvojXrb41Aw", client: "Bolt", title: "20-sekunders performance i feed", impact: "Snabb kampanjproduktion i 9:16 för paid social med tydligt produktbudskap.", tag: "Pulse · Performance", metrics: [{ label: "Format", value: "9:16 paid" }, { label: "Leverans", value: "< 14 dgr" }] },
  { id: "HwWGVOl6dtg", client: "ALSO / Huawei", title: "B2B-berättelse om Energy Solution", impact: "Internationell brandfilm som översätter en teknisk produkt till en mänsklig historia.", tag: "Prime · B2B", metrics: [{ label: "Räckvidd", value: "Internationellt" }, { label: "Roll", value: "Brandfilm" }] },
];

const itemsEn: Item[] = [
  { id: "9iiyNS4yNzA", client: "Garageportexperten", title: "Brand storytelling that opens doors", impact: "Continuous Prime track — video used across sales, onboarding and recruitment.", tag: "Prime · Brand", metrics: [{ label: "Used in", value: "3 channels" }, { label: "Pace", value: "Continuous" }] },
  { id: "yvojXrb41Aw", client: "Bolt", title: "20-second performance in feed", impact: "Fast campaign production in 9:16 for paid social with a sharp product message.", tag: "Pulse · Performance", metrics: [{ label: "Format", value: "9:16 paid" }, { label: "Delivery", value: "< 14 days" }] },
  { id: "HwWGVOl6dtg", client: "ALSO / Huawei", title: "B2B story about Energy Solution", impact: "International brand film that turns a technical product into a human story.", tag: "Prime · B2B", metrics: [{ label: "Reach", value: "International" }, { label: "Role", value: "Brand film" }] },
];

export function ProofStrip() {
  const en = useLocale() === "en";
  const items = en ? itemsEn : itemsSv;
  return (
    <Section>
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <EyebrowHeading
          eyebrow={en ? "Proof" : "Proof"}
          title={en ? "Video that actually delivers." : "Filmer som faktiskt levererar."}
          lede={en
            ? "Three examples — from continuous Pulse flows to bespoke Prime productions."
            : "Tre exempel — från löpande Pulse-flöden till skräddarsydda Prime-produktioner."}
        />
        <Link
          to={en ? "/en/work" : "/case"}
          className="inline-flex items-center gap-1.5 self-start text-sm font-semibold text-mint hover:text-mint/80 md:self-end"
        >
          {en ? "See all work" : "Se alla case"} <ArrowRight className="h-4 w-4" />
        </Link>
      </div>

      <div className="mt-12 grid gap-5 md:grid-cols-3">
        {items.map((it) => (
          <Link
            key={it.id}
            to={en ? "/en/work" : "/case"}
            className="group flex flex-col overflow-hidden rounded-3xl border border-white/10 bg-surface transition-colors hover:border-mint/40"
          >
            <div className="relative aspect-video overflow-hidden bg-black">
              <img
                src={videoThumb(it.id)}
                width={480}
                height={360}
                alt={it.client}
                loading="lazy"
                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/0 to-black/0" />
              <div className="absolute left-4 top-4 rounded-full bg-black/60 px-3 py-1 text-[11px] uppercase tracking-widest text-white/90 backdrop-blur">
                {it.tag}
              </div>
            </div>
            <div className="flex flex-1 flex-col p-6">
              <div className="text-xs uppercase tracking-widest text-muted-foreground">{it.client}</div>
              <h3 className="mt-2 text-lg font-semibold leading-tight">{it.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{it.impact}</p>

              {it.metrics && it.metrics.length > 0 && (
                <dl className="mt-5 grid grid-cols-2 gap-3 rounded-2xl border border-white/10 bg-background/40 p-4">
                  {it.metrics.map((m) => (
                    <div key={m.label}>
                      <dt className="text-[10px] uppercase tracking-widest text-muted-foreground">{m.label}</dt>
                      <dd className="mt-1 text-sm font-semibold text-mint">{m.value}</dd>
                    </div>
                  ))}
                </dl>
              )}

              <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-mint">
                {en ? "See case" : "Se caset"} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </span>
            </div>
          </Link>
        ))}
      </div>
    </Section>
  );
}
